import { useContext, useMemo } from 'react'
import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { ogsmKeys, scorecardKeys } from '@/hooks/query-keys'
import {
  useAddOgsmActualMutation,
  useArchiveOgsmNodeMutation,
  useCreateOgsmNodeMutation,
  useCreateOgsmSessionMutation,
  useDeleteOgsmNodeMutation,
  useMoveOgsmNodeMutation,
  useOgsmActualsQuery,
  useOgsmAssigneesQuery,
  useOgsmBoardQuery,
  useOgsmRemovalImpactQuery,
  useOgsmSessionsQuery,
  useReviseOgsmTargetMutation,
  useUpdateOgsmNodeMutation,
  useUpdateOgsmStatementMutation,
} from '@/hooks/use-ogsm-data'
import {
  addOgsmActual,
  archiveOgsmNode,
  createOgsmNode,
  createOgsmSession,
  deleteOgsmNode,
  getOgsmBoard,
  getOgsmRemovalImpact,
  listOgsmActuals,
  listOgsmAssignees,
  listOgsmSessions,
  moveOgsmNode,
  publishOgsmSession,
  reopenOgsmSession,
  reviseOgsmTarget,
  updateOgsmNode,
  updateOgsmStatement,
} from '@/services/ogsm.service'
import { OgsmApiContext, type OgsmApi } from '@/features/ogsm/ogsm-api'

/** The internal scope: `/companies/:companyId/ogsm/*`, the company's keys and its Scorecard. */
export function companyOgsmApi(companyId: string): OgsmApi {
  return {
    key: ogsmKeys.company(companyId),
    scorecardKey: scorecardKeys.company(companyId),
    metricLinking: true,
    listSessions: () => listOgsmSessions(companyId),
    createSession: () => createOgsmSession(companyId),
    getBoard: (sessionId) => getOgsmBoard(companyId, sessionId),
    updateStatement: (sessionId, statement) =>
      updateOgsmStatement(companyId, sessionId, statement),
    createNode: (sessionId, input) =>
      createOgsmNode(companyId, sessionId, input),
    updateNode: (nodeId, input) => updateOgsmNode(companyId, nodeId, input),
    deleteNode: (nodeId) => deleteOgsmNode(companyId, nodeId),
    getRemovalImpact: (nodeId) => getOgsmRemovalImpact(companyId, nodeId),
    archiveNode: (nodeId) => archiveOgsmNode(companyId, nodeId),
    moveNode: (nodeId, position) => moveOgsmNode(companyId, nodeId, position),
    listAssignees: () => listOgsmAssignees(companyId),
    listActuals: (nodeId) => listOgsmActuals(companyId, nodeId),
    addActual: (nodeId, input) => addOgsmActual(companyId, nodeId, input),
    reviseTarget: (nodeId, input) => reviseOgsmTarget(companyId, nodeId, input),
  }
}

/** The provided scope (the portal's), else the company's own routes. */
export function useOgsmApi(companyId: string): OgsmApi {
  const provided = useContext(OgsmApiContext)
  const company = useMemo(() => companyOgsmApi(companyId), [companyId])
  return provided ?? company
}

const useCompanyApi = (companyId: string) =>
  useMemo(() => companyOgsmApi(companyId), [companyId])

export function useOgsmSessions(companyId: string, enabled = true) {
  return useOgsmSessionsQuery(
    useCompanyApi(companyId),
    enabled && Boolean(companyId)
  )
}

export function useOgsmBoard(companyId: string, sessionId: string | undefined) {
  return useOgsmBoardQuery(
    useCompanyApi(companyId),
    companyId ? sessionId : undefined
  )
}

/** The URL's session or the newest one; see `useActiveOgsmBoardQuery`. */
export function useActiveOgsmBoard(
  companyId: string,
  sessionId: string | undefined
) {
  const sessions = useOgsmSessions(companyId)
  const activeId = sessionId ?? sessions.data?.sessions[0]?.id
  const board = useOgsmBoard(companyId, activeId)
  return { sessions, board, activeId }
}

export function useOgsmAssignees(companyId: string, enabled = true) {
  return useOgsmAssigneesQuery(
    useCompanyApi(companyId),
    enabled && Boolean(companyId)
  )
}

export function useCreateOgsmSession(companyId: string) {
  return useCreateOgsmSessionMutation(useCompanyApi(companyId))
}

/** A publish or reopen changes the session, its board and the Scorecard. */
function invalidateSessionChange(
  queryClient: QueryClient,
  companyId: string,
  sessionId: string
) {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: ogsmKeys.board(companyId, sessionId),
    }),
    queryClient.invalidateQueries({ queryKey: ogsmKeys.sessions(companyId) }),
    queryClient.invalidateQueries({
      queryKey: scorecardKeys.company(companyId),
    }),
  ])
}

/**
 * `onDone` runs from the mutation itself, after the refetch, so it still fires
 * when the dialog was closed (and its `mutate` callbacks dropped) while pending.
 */
export function usePublishOgsmSession(
  companyId: string,
  sessionId: string,
  onDone?: () => void
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => publishOgsmSession(companyId, sessionId),
    onSuccess: async () => {
      await invalidateSessionChange(queryClient, companyId, sessionId)
      onDone?.()
    },
  })
}

/** Same as `usePublishOgsmSession`. */
export function useReopenOgsmSession(
  companyId: string,
  sessionId: string,
  onDone?: () => void
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => reopenOgsmSession(companyId, sessionId),
    onSuccess: async () => {
      await invalidateSessionChange(queryClient, companyId, sessionId)
      onDone?.()
    },
  })
}

export function useUpdateOgsmStatement(companyId: string, sessionId: string) {
  return useUpdateOgsmStatementMutation(useCompanyApi(companyId), sessionId)
}

export function useCreateOgsmNode(companyId: string, sessionId: string) {
  return useCreateOgsmNodeMutation(useCompanyApi(companyId), sessionId)
}

export function useUpdateOgsmNode(companyId: string, sessionId: string) {
  return useUpdateOgsmNodeMutation(useCompanyApi(companyId), sessionId)
}

export function useMoveOgsmNode(companyId: string, sessionId: string) {
  return useMoveOgsmNodeMutation(useCompanyApi(companyId), sessionId)
}

export function useDeleteOgsmNode(companyId: string, sessionId: string) {
  return useDeleteOgsmNodeMutation(useCompanyApi(companyId), sessionId)
}

export function useOgsmRemovalImpact(companyId: string, nodeId: string) {
  return useOgsmRemovalImpactQuery(
    useCompanyApi(companyId),
    companyId ? nodeId : ''
  )
}

export function useArchiveOgsmNode(companyId: string, sessionId: string) {
  return useArchiveOgsmNodeMutation(useCompanyApi(companyId), sessionId)
}

export function useOgsmActuals(
  companyId: string,
  nodeId: string,
  enabled: boolean
) {
  return useOgsmActualsQuery(
    useCompanyApi(companyId),
    nodeId,
    enabled && Boolean(companyId)
  )
}

export function useAddOgsmActual(companyId: string, sessionId: string) {
  return useAddOgsmActualMutation(useCompanyApi(companyId), sessionId)
}

export function useReviseOgsmTarget(companyId: string, sessionId: string) {
  return useReviseOgsmTargetMutation(useCompanyApi(companyId), sessionId)
}
